import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { SAVED_FILTERS } from "../ticker/TickerSavedBar";
import SavedPagerPage from "./SavedPagerPage";
import "./MostVisitedPager.css";

const FILTERS = SAVED_FILTERS.map(({ id }) => id);
const VISUAL_FILTERS = [...FILTERS].reverse();

const dispatchSavedPagerProgress = (progress) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("savedPagerProgress", {
      detail: { progress },
    })
  );
};

/*
|-----------------------------------------------------------------------------
| iOS Saved Pager
|-----------------------------------------------------------------------------
|
| iOS keeps the native horizontal scroll-snap implementation. Safari handles
| the nested horizontal pager and each page's native vertical scroller well,
| so preserving this path avoids changing the already-correct iOS behavior.
|
*/
const SavedPagerIOS = ({
  pages = {},
  favicons = {},
  onMarkAsRead,
  onNewsOpen,
  activeFilter = FILTERS[0],
  onFilterChange,
  scrollToTopSignal,
  onActivePageAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
}) => {
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const scrollTimeoutRef = useRef(null);
  const progressRafRef = useRef(null);
  const pendingProgressRef = useRef(0);
  const internalPageChangeRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, FILTERS.indexOf(activeFilter))
  );

  const activeIndexRef = useRef(activeIndex);

  useLayoutEffect(() => {
    const index = Math.max(0, FILTERS.indexOf(activeFilter));
    const container = containerRef.current;

    if (internalPageChangeRef.current && index === activeIndexRef.current) {
      internalPageChangeRef.current = false;
      return undefined;
    }

    activeIndexRef.current = index;
    setActiveIndex(index);

    const page = pageRefs.current[activeFilter];
    const pageScrollTop = page?.scrollTop || 0;

    onActivePageAtTopChange?.(pageScrollTop === 0);

    if (!container) return undefined;

    const visualIndex = VISUAL_FILTERS.indexOf(activeFilter);
    if (visualIndex < 0) return undefined;

    dispatchSavedPagerProgress(visualIndex);

    const setScrollPosition = () => {
      container.scrollLeft = visualIndex * container.clientWidth;
    };

    setScrollPosition();

    const frameId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(setScrollPosition)
        : null;

    return () => {
      if (frameId !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frameId);
      }
    };
  }, [activeFilter, onActivePageAtTopChange]);

  const activateVisualIndex = useCallback(
    (visualIndex) => {
      const safeVisualIndex = Math.max(
        0,
        Math.min(visualIndex, VISUAL_FILTERS.length - 1)
      );

      const nextFilter = VISUAL_FILTERS[safeVisualIndex];
      const newIndex = FILTERS.indexOf(nextFilter);

      if (!nextFilter || newIndex < 0 || newIndex === activeIndexRef.current) {
        return;
      }

      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);

      const page = pageRefs.current[nextFilter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(pageScrollTop === 0);

      internalPageChangeRef.current = true;
      onFilterChange?.(nextFilter);
    },
    [onActivePageAtTopChange, onFilterChange]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const publishProgress = (progress) => {
      pendingProgressRef.current = progress;

      if (progressRafRef.current !== null) return;

      if (typeof requestAnimationFrame !== "function") {
        dispatchSavedPagerProgress(pendingProgressRef.current);
        return;
      }

      progressRafRef.current = requestAnimationFrame(() => {
        progressRafRef.current = null;
        dispatchSavedPagerProgress(pendingProgressRef.current);
      });
    };

    const handleScrollEnd = () => {
      const pageWidth = container.clientWidth;
      if (!pageWidth) return;

      const visualIndex = Math.round(container.scrollLeft / pageWidth);
      const safeProgress = Math.max(
        0,
        Math.min(visualIndex, VISUAL_FILTERS.length - 1)
      );

      publishProgress(safeProgress);
      activateVisualIndex(visualIndex);
    };

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const pageWidth = container.clientWidth;

      if (pageWidth) {
        const progress = Math.max(
          0,
          Math.min(
            container.scrollLeft / pageWidth,
            VISUAL_FILTERS.length - 1
          )
        );

        publishProgress(progress);
      }

      scrollTimeoutRef.current = setTimeout(handleScrollEnd, 120);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (
        progressRafRef.current !== null &&
        typeof cancelAnimationFrame === "function"
      ) {
        cancelAnimationFrame(progressRafRef.current);
      }
    };
  }, [activateVisualIndex]);

  useEffect(() => {
    if (!scrollToTopSignal) return;

    const activePage = pageRefs.current[FILTERS[activeIndex]];

    activePage?.scrollTo({
      top: 0,
      behavior: "auto",
    });

    onActivePageAtTopChange?.(true);
  }, [activeIndex, onActivePageAtTopChange, scrollToTopSignal]);

  const handlePageScroll = useCallback(
    (filter) => {
      if (filter !== FILTERS[activeIndex]) return;

      const page = pageRefs.current[filter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(!page || pageScrollTop === 0);
    },
    [activeIndex, onActivePageAtTopChange]
  );

  return (
    <div className="most-visited-pager saved-pager saved-pager--ios">
      <div className="most-visited-pager__container" ref={containerRef}>
        {VISUAL_FILTERS.map((filter, visualIndex) => (
          <SavedPagerPage
            key={`${filter}-${visualIndex}`}
            ref={(node) => {
              if (node) {
                pageRefs.current[filter] = node;
              } else {
                delete pageRefs.current[filter];
              }
            }}
            filter={filter}
            pageItems={pages[filter] || []}
            onScroll={() => handlePageScroll(filter)}
            favicons={favicons}
            onMarkAsRead={onMarkAsRead}
            onNewsOpen={onNewsOpen}
            savedLinks={savedLinks}
            onToggleSaved={onToggleSaved}
            onShare={onShare}
            onView={onView}
          />
        ))}
      </div>
    </div>
  );
};

export default SavedPagerIOS;
